// Lieferanten und Partien (E3c, S1 – PLAN.md, „E3c“; G19: „die Sachen
// müssen irgendwie selbsterklärend sein“): Hier trägt die Leitung einmal ein,
// bei wem bestellt wird und wer montiert – bei jeder Anlage tippt sie später
// nur noch an (Bestellung S3, Partien S4). Eine Seite, von oben nach unten:
//   - drei Abschnitte – Lieferanten, Dachpartien, Bodenpartien –, je mit
//     einem Satz, wofür sie sind und wo sie gebraucht werden; je Eintrag Name
//     und Ansprechpartner (Name, Mail, Telefon), „Ändern“ öffnet das Formular
//     an Ort und Stelle, „+ Lieferant“ ein leeres. Gelöscht wird nichts:
//     „nicht mehr anbieten“ – zugeklappt darunter, mit „wieder anbieten“
//     (Prüfung des Plans E3c, Befund 8);
//   - die zwei Mails (an den Lieferanten, an die Dachpartie) mit den
//     Platzhaltern in Worten – solange nichts Eigenes gespeichert ist, gilt
//     der Vorschlag;
//   - der Vorlauf der Lieferung und die Adresse von K2.
//
// Gespeichert wird mit „Speichern“, nicht beim Tippen: Ein Lieferant ist
// mehr als ein Feld, und halb eingetragen soll er nirgends zur Wahl stehen.
// Nach dem Speichern holt die Seite die Liste neu (der Server ordnet).
//
// Adresse: #/partner – nur die Leitung (Recht „partner-pflegen“).

import { useId, useState, type FormEvent, type ReactNode } from "react";
import {
  ABLAUF_GRENZEN,
  MAIL_PLATZHALTER,
  neueKennung,
  PARTNER_ARTEN,
  STANDARD_MAILVORLAGEN,
  unbekanntePlatzhalter,
  type AblaufEinstellungen,
  type AblaufEinstellungenEingabe,
  type BueroAntwort,
  type MailArt,
  type PartnerArt,
  type PartnerAntwort,
  type PartnerZeile,
} from "@happy-energy/kern";
import { Chip, Ikon, senden, toastZeigen } from "@happy-energy/oberflaeche";
import { Laedt, LadeFehler, useLaden } from "../laden.tsx";
import { meldungAus } from "../meldung.ts";
import { Seite } from "../rahmen.tsx";
import {
  ABSCHNITTE,
  eingabeAus,
  entwurfAus,
  hinweisFuer,
  nachArt,
  neuerEntwurf,
  nochEinePerson,
  personText,
  type FeldOrt,
  type PartnerEntwurf,
  type PersonEntwurf,
} from "./partner.ts";
import "../formular.css";
import "./partner.css";

const NUR_LEITUNG = "Lieferanten und Partien pflegt die Leitung.";

// --- Ein Lieferant oder eine Partie: anlegen und ändern ------------------------------------------

function PartnerFormular({ anfang, fertig, abbrechen }: { anfang: PartnerEntwurf; fertig: (name: string) => void; abbrechen: () => void }) {
  const [e, setE] = useState(anfang);
  const [fehler, setFehler] = useState<{ ort: FeldOrt; meldung: string } | null>(null);
  const [sendet, setSendet] = useState(false);
  const a = ABSCHNITTE[e.art];
  const titelId = useId();

  const person = (nr: number, teil: keyof PersonEntwurf, wert: string) =>
    setE((alt) => ({ ...alt, ansprechpartner: alt.ansprechpartner.map((p, i) => (i === nr ? { ...p, [teil]: wert } : p)) }));
  const amFeld = (nr: number, teil: keyof PersonEntwurf) => fehler?.ort.feld === "person" && fehler.ort.nr === nr && fehler.ort.teil === teil;

  async function speichern(ev: FormEvent) {
    ev.preventDefault();
    const pruefung = eingabeAus(e);
    if (!pruefung.ok) {
      setFehler({ ort: pruefung.ort, meldung: pruefung.meldung });
      return;
    }
    setFehler(null);
    setSendet(true);
    try {
      await senden<BueroAntwort>("/api/crm/partner", pruefung.eingabe);
      fertig(pruefung.eingabe.name);
    } catch (f) {
      setFehler({ ort: { feld: null }, meldung: meldungAus(f, NUR_LEITUNG) });
      setSendet(false);
    }
  }

  return (
    <form
      className="partner-formular"
      aria-labelledby={titelId}
      onSubmit={(ev) => void speichern(ev)}
      onKeyDown={(ev) => {
        if (ev.key === "Escape" && !sendet) abbrechen();
      }}
    >
      <h3 id={titelId}>{e.neu ? a.neu : `${anfang.name} ändern`}</h3>
      <label className="crm-feld partner-name">
        Name {e.art === "lieferant" ? "der Firma" : "der Partie"}
        <input
          className="crm-eingabe"
          value={e.name}
          autoFocus
          maxLength={ABLAUF_GRENZEN.name}
          placeholder={a.beispiel}
          aria-invalid={fehler?.ort.feld === "name" ? true : undefined}
          onChange={(ev) => setE({ ...e, name: ev.target.value })}
        />
        {fehler?.ort.feld === "name" && <span className="crm-fehler-satz">{fehler.meldung}</span>}
      </label>

      <div className="crm-feld-gruppe">
        <span className="crm-feld-titel">
          Ansprechpartner {a.mitMail ? <span className="freiwillig">– an sie geht später die Mail; der Name steht dort nach „Hallo“</span> : <span className="freiwillig">(wenn ihr wollt)</span>}
        </span>
        <ul className="partner-personen">
          {e.ansprechpartner.map((p, nr) => (
            <li key={nr} className="partner-person">
              <label className="crm-feld">
                Name
                <input
                  className="crm-eingabe"
                  value={p.name}
                  maxLength={ABLAUF_GRENZEN.name}
                  placeholder="z. B. Anes"
                  aria-invalid={amFeld(nr, "name") ? true : undefined}
                  onChange={(ev) => person(nr, "name", ev.target.value)}
                />
              </label>
              <label className="crm-feld">
                <span>E-Mail {!a.mitMail && <span className="freiwillig">(freiwillig)</span>}</span>
                <input
                  className="crm-eingabe"
                  type="email"
                  value={p.email}
                  maxLength={200}
                  placeholder="name@firma.at"
                  aria-invalid={amFeld(nr, "email") ? true : undefined}
                  onChange={(ev) => person(nr, "email", ev.target.value)}
                />
              </label>
              <label className="crm-feld">
                <span>
                  Telefon <span className="freiwillig">(freiwillig)</span>
                </span>
                <input
                  className="crm-eingabe"
                  type="tel"
                  value={p.telefon}
                  maxLength={ABLAUF_GRENZEN.telefon}
                  aria-invalid={amFeld(nr, "telefon") ? true : undefined}
                  onChange={(ev) => person(nr, "telefon", ev.target.value)}
                />
              </label>
              <button
                type="button"
                className="crm-knopf leise klein partner-person-weg"
                title="Diesen Ansprechpartner entfernen"
                onClick={() => setE({ ...e, ansprechpartner: e.ansprechpartner.filter((_, i) => i !== nr) })}
              >
                entfernen
              </button>
              {fehler?.ort.feld === "person" && fehler.ort.nr === nr && <span className="crm-fehler-satz partner-person-meldung">{fehler.meldung}</span>}
            </li>
          ))}
        </ul>
        {nochEinePerson(e) && (
          <div>
            <button type="button" className="crm-knopf rand klein" onClick={() => setE({ ...e, ansprechpartner: [...e.ansprechpartner, { name: "", email: "", telefon: "" }] })}>
              <Ikon name="plus" groesse="k" /> Ansprechpartner
            </button>
          </div>
        )}
      </div>

      {fehler && fehler.ort.feld === null && (
        <p className="crm-fehler-satz" role="alert">
          {fehler.meldung}
        </p>
      )}
      <div className="crm-knopf-reihe">
        <button type="submit" className="crm-knopf lime" disabled={sendet}>
          {sendet ? <span className="kreisel" aria-hidden="true" /> : <Ikon name="haken" groesse="k" />} Speichern
        </button>
        <button type="button" className="crm-knopf rand" onClick={abbrechen} disabled={sendet}>
          Abbrechen
        </button>
      </div>
    </form>
  );
}

function PartnerEintrag({ p, aendern, anbieten, beschaeftigt }: { p: PartnerZeile; aendern: () => void; anbieten: (aktiv: boolean) => void; beschaeftigt: boolean }) {
  const hinweis = hinweisFuer(p);
  return (
    <li className="partner-eintrag">
      <div className="partner-wort">
        <b>{p.name}</b>
        {p.ansprechpartner.length > 0 ? (
          p.ansprechpartner.map((a, i) => <span key={i}>{personText(a)}</span>)
        ) : (
          <span className="leise">kein Ansprechpartner eingetragen</span>
        )}
        {hinweis && (
          <span className="partner-hinweis">
            <Ikon name="achtung" groesse="k" /> {hinweis}
          </span>
        )}
      </div>
      <div className="partner-knoepfe">
        <button type="button" className="crm-knopf rand klein" onClick={aendern} disabled={beschaeftigt}>
          <Ikon name="stift" groesse="k" /> Ändern
        </button>
        <button
          type="button"
          className="crm-knopf leise klein"
          onClick={() => anbieten(false)}
          disabled={beschaeftigt}
          title="Steht bei neuen Anlagen nicht mehr zur Wahl – was schon bestellt oder eingeteilt ist, bleibt"
        >
          nicht mehr anbieten
        </button>
      </div>
    </li>
  );
}

/** Was nicht mehr angeboten wird – zugeklappt, wie „Herausgenommen“ beim Standardmaterial. */
function NichtMehrAngeboten({ zeilen, anbieten, beschaeftigt }: { zeilen: PartnerZeile[]; anbieten: (p: PartnerZeile) => void; beschaeftigt: string | null }) {
  const [auf, setAuf] = useState(false);
  const inhalt = useId();
  return (
    <div className="partner-inaktiv">
      <button type="button" className="std-klapp partner-klapp" aria-expanded={auf} aria-controls={inhalt} onClick={() => setAuf(!auf)}>
        <Ikon name="rechts" groesse="k" />
        <span>
          Nicht mehr angeboten <span className="std-klapp-zahl">({zeilen.length})</span>
        </span>
      </button>
      <div id={inhalt} hidden={!auf}>
        <p className="leise klein">Bleibt erhalten, damit alte Bestellungen und Einsätze stimmen. Steht bei neuen Anlagen nicht zur Wahl.</p>
        <ul className="std-inaktiv-liste">
          {zeilen.map((p) => (
            <li key={p.id}>
              <span className="std-inaktiv-wort">
                <b>{p.name}</b>
                {p.ansprechpartner.length > 0 && ` · ${p.ansprechpartner.map((a) => a.name).join(", ")}`}
              </span>
              <button type="button" className="crm-knopf rand klein" onClick={() => anbieten(p)} disabled={beschaeftigt !== null}>
                <Ikon name="zurueck" groesse="k" /> wieder anbieten
              </button>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

function PartnerAbschnitt({ art, aktiv, inaktiv, neuLaden }: { art: PartnerArt; aktiv: PartnerZeile[]; inaktiv: PartnerZeile[]; neuLaden: () => void }) {
  const a = ABSCHNITTE[art];
  // Was gerade im Formular ist: ein neuer Eintrag oder einer zum Ändern (seine Kennung).
  const [formular, setFormular] = useState<PartnerEntwurf | null>(null);
  const [beschaeftigt, setBeschaeftigt] = useState<string | null>(null);
  const [meldung, setMeldung] = useState<string | null>(null);

  function gespeichert(name: string, neu: boolean) {
    setFormular(null);
    toastZeigen(neu ? `${name} ist angelegt` : `${name} ist gespeichert`);
    neuLaden();
  }

  async function anbieten(p: PartnerZeile, wieder: boolean) {
    setBeschaeftigt(p.id);
    setMeldung(null);
    try {
      await senden<BueroAntwort>("/api/crm/partner", { id: p.id, art: p.art, name: p.name, ansprechpartner: p.ansprechpartner, aktiv: wieder });
      toastZeigen(wieder ? `${p.name} steht wieder zur Wahl` : `${p.name} wird nicht mehr angeboten – unten unter „Nicht mehr angeboten“ holst du ihn zurück`);
      neuLaden();
    } catch (f) {
      setMeldung(meldungAus(f, NUR_LEITUNG));
    } finally {
      setBeschaeftigt(null);
    }
  }

  return (
    <section className="crm-karte partner-abschnitt" aria-label={a.titel}>
      <div className="karten-kopf">
        <Ikon name={a.ikon} />
        <h2>{a.titel}</h2>
        <Chip>{aktiv.length === 1 ? "1 Eintrag" : `${aktiv.length} Einträge`}</Chip>
      </div>
      <p className="leise klein partner-satz">{a.satz}</p>
      {aktiv.length > 0 ? (
        <ul className="partner-liste">
          {aktiv.map((p) =>
            formular?.id === p.id ? (
              <li key={p.id} className="partner-eintrag offen">
                <PartnerFormular anfang={formular} fertig={(name) => gespeichert(name, false)} abbrechen={() => setFormular(null)} />
              </li>
            ) : (
              <PartnerEintrag key={p.id} p={p} aendern={() => setFormular(entwurfAus(p))} anbieten={() => void anbieten(p, false)} beschaeftigt={beschaeftigt === p.id} />
            ),
          )}
        </ul>
      ) : (
        !formular && <p className="leise klein partner-satz">Noch keine – „+ {a.knopf}“ legt die erste an.</p>
      )}
      {formular?.neu ? (
        <div className="partner-eintrag offen">
          <PartnerFormular anfang={formular} fertig={(name) => gespeichert(name, true)} abbrechen={() => setFormular(null)} />
        </div>
      ) : (
        <div>
          <button type="button" className="crm-knopf rand klein" onClick={() => setFormular(neuerEntwurf(neueKennung(), art))} title={`Neuen Eintrag unter „${a.titel}“ anlegen`}>
            <Ikon name="plus" groesse="k" /> {a.knopf}
          </button>
        </div>
      )}
      {meldung && (
        <p className="crm-fehler-satz" role="alert">
          {meldung}
        </p>
      )}
      {inaktiv.length > 0 && <NichtMehrAngeboten zeilen={inaktiv} anbieten={(p) => void anbieten(p, true)} beschaeftigt={beschaeftigt} />}
    </section>
  );
}

// --- Die Vorgaben: Mails, Vorlauf, K2 ------------------------------------------------------------

/** Speichert einen Teil der Vorgaben – gibt die Meldung zurück, wenn es nicht ging (sonst null). */
async function vorgabenSpeichern(eingabe: AblaufEinstellungenEingabe, erfolg: string): Promise<string | null> {
  try {
    await senden<BueroAntwort>("/api/crm/firma/ablauf", eingabe);
    toastZeigen(erfolg);
    return null;
  } catch (f) {
    return meldungAus(f, "Die Vorgaben der Firma ändert die Leitung.");
  }
}

const MAIL_TITEL: Record<MailArt, { titel: string; satz: string }> = {
  bestellung: {
    titel: "Mail an den Lieferanten",
    satz: "Mit diesem Text öffnet das CRM bei der Bestellung die Mail – an den Ansprechpartner des Lieferanten, mit allem schon ausgefüllt. Die Dateien hängst du selbst an und schickst sie dann ab.",
  },
  dachpartie: {
    titel: "Mail an die Dachpartie",
    satz: "Mit diesem Text öffnet das CRM die Mail an die Dachpartie – mit Termin, Adresse und den Modulen. Die Unterlage fürs Dach hängst du an.",
  },
};

/**
 * Eine Mail zum Ändern. Kommt vom Server ein anderer Stand (nach dem
 * Speichern), beginnt der Block neu – der Aufrufer gibt ihm dafür den Stand
 * als `key`.
 */
function MailBlock({ art, gilt, neuLaden }: { art: MailArt; gilt: AblaufEinstellungen["mailvorlagen"][MailArt]; neuLaden: () => void }) {
  const [betreff, setBetreff] = useState(gilt.betreff);
  const [text, setText] = useState(gilt.text);
  const [sendet, setSendet] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);

  const geaendert = betreff !== gilt.betreff || text !== gilt.text;
  const unbekannt = unbekanntePlatzhalter(`${betreff}\n${text}`, art);
  const t = MAIL_TITEL[art];

  async function speichern(vorlage: { betreff: string; text: string } | null, erfolg: string) {
    setSendet(true);
    setFehler(await vorgabenSpeichern({ mailvorlagen: { [art]: vorlage } }, erfolg));
    setSendet(false);
    neuLaden();
  }

  return (
    <div className="partner-mail" role="group" aria-label={t.titel}>
      <div className="partner-mail-kopf">
        <h3>{t.titel}</h3>
        {gilt.vorschlag && !geaendert && <Chip>Vorschlag</Chip>}
        {!gilt.vorschlag && !geaendert && <Chip art="lime">eigener Text</Chip>}
        {geaendert && <Chip art="warn">noch nicht gespeichert</Chip>}
      </div>
      <p className="leise klein partner-satz">{t.satz}</p>
      <label className="crm-feld">
        Betreff
        <input className="crm-eingabe" value={betreff} maxLength={ABLAUF_GRENZEN.betreff} onChange={(ev) => setBetreff(ev.target.value)} />
      </label>
      <label className="crm-feld">
        Text
        <textarea className="partner-text" value={text} maxLength={ABLAUF_GRENZEN.mailtext} rows={12} onChange={(ev) => setText(ev.target.value)} />
      </label>
      <div className="partner-platzhalter">
        <span className="crm-feld-titel">Diese Wörter in eckigen Klammern füllt das CRM bei jeder Anlage selbst aus:</span>
        <ul>
          {MAIL_PLATZHALTER[art].map((p) => (
            <li key={p.wort}>
              <code>[{p.wort}]</code> {p.heisst}
            </li>
          ))}
        </ul>
      </div>
      {unbekannt.length > 0 && (
        <p className="partner-hinweis">
          <Ikon name="achtung" groesse="k" /> {unbekannt.map((w) => `[${w}]`).join(", ")} {unbekannt.length === 1 ? "kennt" : "kennen"} das CRM nicht – das bleibt so in der Mail stehen. Vertippt?
        </p>
      )}
      {fehler && (
        <p className="crm-fehler-satz" role="alert">
          {fehler}
        </p>
      )}
      <div className="crm-knopf-reihe">
        <button type="button" className="crm-knopf lime klein" disabled={!geaendert || sendet} onClick={() => void speichern({ betreff, text }, `${t.titel}: gespeichert`)}>
          {sendet ? <span className="kreisel" aria-hidden="true" /> : <Ikon name="haken" groesse="k" />} Text speichern
        </button>
        {geaendert && (
          <button
            type="button"
            className="crm-knopf leise klein"
            disabled={sendet}
            onClick={() => {
              setBetreff(gilt.betreff);
              setText(gilt.text);
            }}
          >
            Änderung verwerfen
          </button>
        )}
        {!gilt.vorschlag && !geaendert && (
          <button
            type="button"
            className="crm-knopf leise klein"
            disabled={sendet}
            title={`Zurück zum Vorschlag: „${STANDARD_MAILVORLAGEN[art].betreff}“ …`}
            onClick={() => void speichern(null, `${t.titel}: wieder der Vorschlag`)}
          >
            Zurück zum Vorschlag
          </button>
        )}
      </div>
    </div>
  );
}

/** Ein Feld mit eigenem „Speichern“ – für Vorlauf und K2. */
function EinFeld({
  titel,
  satz,
  children,
  geaendert,
  sendet,
  speichern,
  fehler,
}: {
  titel: string;
  satz: ReactNode;
  children: ReactNode;
  geaendert: boolean;
  sendet: boolean;
  speichern: () => void;
  fehler: string | null;
}) {
  return (
    <form
      className="partner-feld"
      aria-label={titel}
      onSubmit={(ev) => {
        ev.preventDefault();
        if (geaendert) speichern();
      }}
    >
      <h3>{titel}</h3>
      <p className="leise klein partner-satz">{satz}</p>
      <div className="partner-feld-zeile">
        {children}
        <button type="submit" className="crm-knopf lime klein" disabled={!geaendert || sendet}>
          {sendet ? <span className="kreisel" aria-hidden="true" /> : <Ikon name="haken" groesse="k" />} Speichern
        </button>
      </div>
      {fehler && (
        <p className="crm-fehler-satz" role="alert">
          {fehler}
        </p>
      )}
    </form>
  );
}

function VorlaufFeld({ gilt, neuLaden }: { gilt: number; neuLaden: () => void }) {
  const [vorlauf, setVorlauf] = useState(String(gilt));
  const [sendet, setSendet] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);
  const tage = Number(vorlauf);
  const gueltig = vorlauf.trim() !== "" && Number.isInteger(tage) && tage >= 0 && tage <= ABLAUF_GRENZEN.vorlaufMax;
  return (
    <EinFeld
      titel="Wann geliefert wird"
      satz="Der Vorschlag für den Wunschliefertermin – bei jeder Bestellung kannst du ihn noch ändern. Werktage: Montag bis Freitag – Feiertage rechnet das CRM nicht heraus."
      geaendert={gueltig && tage !== gilt}
      sendet={sendet}
      fehler={fehler ?? (vorlauf.trim() !== "" && !gueltig ? `Bitte eine ganze Zahl von 0 bis ${ABLAUF_GRENZEN.vorlaufMax}.` : null)}
      speichern={() => {
        setSendet(true);
        void vorgabenSpeichern({ lieferVorlaufWerktage: tage }, "Vorlauf der Lieferung gespeichert").then((meldung) => {
          setFehler(meldung);
          setSendet(false);
          neuLaden();
        });
      }}
    >
      <label className="crm-feld partner-vorlauf">
        <span>Lieferung</span>
        <input className="crm-eingabe" inputMode="numeric" value={vorlauf} maxLength={2} onChange={(ev) => setVorlauf(ev.target.value)} aria-label="Werktage vor dem Montagetermin" />
        <span>{tage === 1 ? "Werktag" : "Werktage"} vor dem Montagetermin</span>
      </label>
    </EinFeld>
  );
}

function K2Feld({ gilt, neuLaden }: { gilt: string | null; neuLaden: () => void }) {
  const [k2, setK2] = useState(gilt ?? "");
  const [sendet, setSendet] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);
  const neu = k2.trim() === "" ? null : k2.trim();
  return (
    <EinFeld
      titel="Die Adresse von K2"
      satz="Damit öffnet „Dachplanung starten“ in der Doku-App gleich K2 – die Adresse, die du sonst im Browser eintippst."
      geaendert={neu !== gilt}
      sendet={sendet}
      fehler={fehler}
      speichern={() => {
        setSendet(true);
        void vorgabenSpeichern({ k2Adresse: neu }, neu === null ? "Adresse von K2 entfernt" : "Adresse von K2 gespeichert").then((meldung) => {
          setFehler(meldung);
          setSendet(false);
          neuLaden();
        });
      }}
    >
      <label className="crm-feld partner-k2">
        Adresse
        <input className="crm-eingabe" type="url" value={k2} maxLength={ABLAUF_GRENZEN.k2Adresse} placeholder="https://…" onChange={(ev) => setK2(ev.target.value)} />
      </label>
    </EinFeld>
  );
}

/** Die Vorgaben der Firma. Jeder Block beginnt neu, wenn vom Server ein anderer Stand kommt (`key`) – so gilt nach dem Speichern, was gespeichert ist. */
function Vorgaben({ v, neuLaden }: { v: AblaufEinstellungen; neuLaden: () => void }) {
  const stand = (m: { betreff: string; text: string; vorschlag: boolean }) => `${m.vorschlag}|${m.betreff}|${m.text}`;
  return (
    <section className="crm-karte partner-abschnitt" aria-label="Mails und Vorgaben">
      <div className="karten-kopf">
        <Ikon name="senden" />
        <h2>Mails und Vorgaben</h2>
      </div>
      <p className="leise klein partner-satz">Gilt für jede Anlage. Ändern kannst du es jederzeit – Mails, die schon draußen sind, bleiben, wie sie waren.</p>
      <div className="partner-mails">
        <MailBlock key={stand(v.mailvorlagen.bestellung)} art="bestellung" gilt={v.mailvorlagen.bestellung} neuLaden={neuLaden} />
        <MailBlock key={stand(v.mailvorlagen.dachpartie)} art="dachpartie" gilt={v.mailvorlagen.dachpartie} neuLaden={neuLaden} />
      </div>
      <div className="partner-felder">
        <VorlaufFeld key={v.lieferVorlaufWerktage} gilt={v.lieferVorlaufWerktage} neuLaden={neuLaden} />
        <K2Feld key={v.k2Adresse ?? ""} gilt={v.k2Adresse} neuLaden={neuLaden} />
      </div>
    </section>
  );
}

// --- Die Seite -----------------------------------------------------------------------------------

export function PartnerSeite() {
  const partner = useLaden<PartnerAntwort>("/api/crm/partner");
  const vorgaben = useLaden<AblaufEinstellungen>("/api/crm/firma/ablauf");
  const je = partner.daten ? nachArt(partner.daten.partner) : null;

  return (
    <Seite titel="Lieferanten und Partien" unter="Bei wem ihr bestellt und wer montiert – einmal eintragen, bei jeder Anlage nur noch antippen" knoepfe={<span />}>
      {/* Seit S7: statt des gelben Hinweises (er sagte dasselbe wie die Zeile oben und jeder Abschnitt) nur, was sonst nirgends steht. */}
      <p className="leise klein partner-oben">Gelöscht wird nichts: Wen ihr nicht mehr braucht, „nicht mehr anbieten“ – alte Bestellungen bleiben, wie sie waren.</p>
      {partner.fehler !== undefined && <LadeFehler fehler={partner.fehler} nochmal={partner.neu} />}
      {!je && partner.fehler === undefined && <Laedt text="Lieferanten und Partien werden geladen …" />}
      {je && PARTNER_ARTEN.map((art) => <PartnerAbschnitt key={art} art={art} aktiv={je[art].aktiv} inaktiv={je[art].inaktiv} neuLaden={partner.neu} />)}
      {vorgaben.fehler !== undefined && <LadeFehler fehler={vorgaben.fehler} nochmal={vorgaben.neu} />}
      {vorgaben.daten && <Vorgaben v={vorgaben.daten} neuLaden={vorgaben.neu} />}
    </Seite>
  );
}
